import type { ReactNode } from "react";

export function Cartao(
  { titulo, pergunta, acoes, children }:
  { titulo?: string; pergunta?: string; acoes?: ReactNode; children: ReactNode },
) {
  return (
    <section className="cartao">
      {(titulo || acoes) && (
        <div className="cartao__topo">
          <div>
            {titulo && <h2 className="cartao__titulo">{titulo}</h2>}
            {pergunta && <p className="cartao__pergunta">{pergunta}</p>}
          </div>
          {acoes && <div className="cartao__acoes">{acoes}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Vazio({ titulo, children }: { titulo: string; children?: ReactNode }) {
  return (
    <div className="vazio">
      <div className="vazio__titulo">{titulo}</div>
      {children}
    </div>
  );
}

export type TomSelo = "ok" | "aviso" | "critico" | "info" | "neutro";

/**
 * Cor de status SEMPRE com a palavra. Cor sozinha nao e encoding: quem nao
 * distingue as cores fica sem a informacao, e num print em preto e branco
 * todos ficam.
 */
export function Selo({ tom, children }: { tom: TomSelo; children: ReactNode }) {
  return <span className={`selo selo--${tom}`}>{children}</span>;
}

const TOM_STATUS: Record<string, TomSelo> = {
  ativa: "ok", suspensa: "aviso", revogada: "critico",
  encerrada: "neutro", liberada: "info", bloqueada: "critico",
};
export function SeloStatus({ status }: { status: string }) {
  return <Selo tom={TOM_STATUS[status] ?? "neutro"}>{status}</Selo>;
}

const TOM_SEVERIDADE: Record<string, TomSelo> = {
  critica: "critico", alta: "critico", media: "aviso", baixa: "neutro",
};
export function SeloSeveridade({ severidade }: { severidade: string }) {
  return <Selo tom={TOM_SEVERIDADE[severidade] ?? "neutro"}>{severidade}</Selo>;
}

const ROTULO_SITUACAO: Record<string, [TomSelo, string]> = {
  saudavel: ["ok", "saudavel"],
  atencao: ["aviso", "atencao"],
  silenciosa: ["critico", "silenciosa"],
  nunca_ativou: ["aviso", "nunca ativou"],
  arquivada: ["neutro", "arquivada"],
};
export function SeloSituacao({ situacao }: { situacao: string }) {
  const [tom, texto] = ROTULO_SITUACAO[situacao] ?? (["neutro", situacao] as [TomSelo, string]);
  return <Selo tom={tom}>{texto}</Selo>;
}

/**
 * Razao contra um limite. Cor de status SO ao cruzar 100%, e ai com a palavra
 * -- um gradiente semaforo em 70/90/100 seria colorir por valor, gastando o
 * canal de cor para reencodar o que o comprimento ja mostra.
 */
export function Medidor(
  { rotulo, usado, total, unidade, nota }:
  { rotulo: string; usado: number; total: number; unidade: string; nota?: string },
) {
  const acima = total > 0 && usado > total;
  const pct = total > 0 ? Math.min(100, (usado / total) * 100) : 0;
  return (
    <div className={`medidor${acima ? " medidor--acima" : ""}`}>
      <div className="medidor__topo">
        <span className="medidor__rotulo">{rotulo}</span>
        <span className="medidor__valor">{usado} de {total} {unidade}</span>
      </div>
      <div className="medidor__trilha">
        <div className="medidor__preenchida" style={{ width: `${pct}%` }} />
      </div>
      {(nota || acima) && (
        <span className="medidor__nota">
          {acima ? "acima do contratado" : nota}
        </span>
      )}
    </div>
  );
}

/** Versao de tabela: medidor pequeno COM o numero ao lado, nunca so a barra. */
export function MicroMedidor({ usado, total }: { usado: number; total: number }) {
  const acima = total > 0 && usado > total;
  const pct = total > 0 ? Math.min(100, (usado / total) * 100) : 0;
  return (
    <span className={`micro-medidor${acima ? " micro-medidor--acima" : ""}`}>
      <span className="micro-medidor__trilha">
        <span className="micro-medidor__preenchida" style={{ width: `${pct}%` }} />
      </span>
      <span className="micro-medidor__num">{usado}/{total}</span>
    </span>
  );
}

export type Fatia = { rotulo: string; total: number };

/**
 * Distribuicao de categorias.
 *
 * Todas as barras da MESMA cor por padrao: as categorias sao nominais, e
 * colorir por valor gastaria o canal de identidade reencodando o comprimento.
 * `enfase` destaca uma e apaga o resto -- e a resposta honesta para "quem esta
 * atrasado"; `critico` marca o balde sobre o qual existe alerta.
 */
export function Barras(
  { fatias, enfase, critico, aoClicar }:
  {
    fatias: Fatia[]; enfase?: string; critico?: string;
    aoClicar?: (rotulo: string) => void;
  },
) {
  const maior = Math.max(1, ...fatias.map((f) => f.total));
  return (
    <div className="barras">
      {fatias.map((f) => {
        const apagada = enfase !== undefined && f.rotulo !== enfase;
        const cls = `barra${apagada ? " barra--apagada" : ""}${f.rotulo === critico ? " barra--critica" : ""}`;
        const conteudo = (
          <>
            <div className="barra__topo">
              <span className="barra__rotulo">
                {f.rotulo}{f.rotulo === critico ? " · critico" : ""}
              </span>
              <span className="barra__total">{f.total}</span>
            </div>
            <div className="barra__trilha">
              <div className="barra__preenchida" style={{ width: `${(f.total / maior) * 100}%` }} />
            </div>
          </>
        );
        return aoClicar ? (
          <button key={f.rotulo} className={`${cls} barra__link`} type="button"
                  onClick={() => aoClicar(f.rotulo)}
                  style={{ background: "none", border: 0, padding: 0, textAlign: "left", cursor: "pointer" }}>
            {conteudo}
          </button>
        ) : (
          <div key={f.rotulo} className={cls}>{conteudo}</div>
        );
      })}
    </div>
  );
}
